import { UUID } from 'node:crypto';
import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from '@environments/environment';
import { EMPTY, expand, finalize, forkJoin, map, mergeMap, Observable, of, Subject, switchMap, take, takeUntil, tap, toArray } from 'rxjs';
import { GetItemOperationsByTerminalAndBatchQuery } from '../../graphql/queries/get-itemoperation';
import { CompleteItemOperationSubscription } from '../../graphql/subscriptions/on-complete-item-operation';
import { DeleteBatchSubscription } from '../../graphql/subscriptions/on-delete-batch';
import { ReorderItemOperationSubscription } from '../../graphql/subscriptions/on-reorder-item-operation';
import { UpdateBatchSubscription } from '../../graphql/subscriptions/on-update-batch';
import { IBatchStatus, integerToBatchStatus } from '../../mes/mes-batch/mes-batch-service';
import { IProductionNotePostRequest, IProductionNoteResponse } from './production-note.service';
import { LocalStorageService, SessionStorageService } from './storage.service';
import { ICompletionGroupType } from './terminalAdministration.service';
import { IAccount } from './user.service';

// #region models

interface IRESTPaginatedItemOperationResponse {
	items: IItemOperationResponse[];
	nextToken: string | null;
}

export interface IDetailData {
	alias: string;
	data: string;
}

export interface ItemOperationGroup {
	groupSortKey: string;
	name: string;
	items: IItemOperation[];
}

interface IPaginatedItemOperationResponse {
	itemOperations: {
		items: IItemOperationResponse[];
		nextToken: string | null;
	};
}

interface IBaseItemOperation {
	id: UUID;
	sourceId?: UUID;
	nextId?: UUID;
	alias: string;
	terminalId?: UUID;
	batchId?: UUID;
	calculatedMillisecondsPerItem: number;
	stockItems: IStockItem[]; // Hingings, paint, screws etc.
	detailAlias?: string; // Reference to the data table.
	group?: string; // Group by property
	groupSortKey?: string;
	items: IItemOperationItem[];
}

interface IItemOperationItem {
	sequenceNumber: number;
	reordered: boolean;
	completed: boolean;
	ready: boolean;
	location?: IItemOperationItemLocation;
}

interface IItemOperationItemLocation {
	name: string;
	placement: number[];
}

export interface IItemOperation extends IBaseItemOperation {
	itemData?: { [key: string]: any };
}

export interface IItemOperationResponse extends IBaseItemOperation {
	itemData?: string;
}

export interface IItemOperationByNextIdResponse extends IItemOperation {
	itemDataFromPredecessors?: { [key: string]: string | null };
}

export interface IItemOperationByIdResponse {
	result: IItemOperation;
	statusCode: number;
}

export interface ItemStatus {
	items: number;
	completed: number;
	reordered: number;
	ready: number;
}

export interface IStockItem {
	stockItemKey: string;
	description?: string;
	amount: number;
	unit: string;
}

export interface BatchResponse {
	id: UUID;
	alias: string;
	name: string;
	status: any;
}

export interface IReorderedItemOperationResponse {
	result?: {
		reorderItemOperation: {
			id: UUID;
			sequenceNumber: number;
			reorderItems: { item: string }[];
			itemOperation: IItemOperation;
		};
		productionNote: IProductionNoteResponse;
	};
	statusCode: number;
}

export interface ICompletedItemOperationResponse {
	result?: {
		id: UUID;
		sequenceNumbers: Array<number>;
		itemOperation: IItemOperation;
	};
	statusCode: number;
}

// #endregion

@Injectable({
	providedIn: 'root',
})
export class ProductionDataService {
	public loadingData = false;
	public completionGroupType: ICompletionGroupType = ICompletionGroupType.Groups;
	private showNonReady = false;
	public settings = {
		getDetails: true,
	};

	constructor(
		private http: HttpClient,
		private sessionStorageService: SessionStorageService,
		private getItemOperationsQuery: GetItemOperationsByTerminalAndBatchQuery,
		private onItemOperationComplete: CompleteItemOperationSubscription,
		private onItemOperationReorder: ReorderItemOperationSubscription,
		private onBatchUpdate: UpdateBatchSubscription,
		private onBatchDelete: DeleteBatchSubscription,
		private localStorageService: LocalStorageService,
	) {
		this.onItemOperationComplete.subscribe$().subscribe(({ onCompleteItemOperation }) => {
			this.handleGraphQLEventItemOperationUpdated(onCompleteItemOperation);
		});

		this.onItemOperationReorder.subscribe$().subscribe(({ onReorderItemOperation }) => {
			this.handleGraphQLEventItemOperationUpdated(onReorderItemOperation);
		});

		this.onBatchUpdate.subscribe$().subscribe(({ onUpdateBatch }) => {
			const batch = onUpdateBatch as BatchResponse;
			const isProduction = integerToBatchStatus(batch.status) === IBatchStatus.Production;

			if (isProduction && this.terminalId) {
				this.upsertBatch(batch); // Add to batches
				const key = `${this.terminalId}#show-non-ready`;
				this.showNonReady = this.localStorageService.get(key) === 'true';
				const status = this.showNonReady ? 'all' : 'someready';
				this.fetchItemOperationsForBatch(this.terminalId, batch.id, status)
					.pipe(take(1), takeUntil(this._terminalCancel$))
					.subscribe((groups) => {
						if (groups.length) {
							this.addItemOperationsDynamically(groups);
						}
					});
			} else {
				this.removeItemOperationsByBatchId(batch.id);
				this.removeBatch(batch.id);
			}
		});

		this.onBatchDelete.subscribe$().subscribe(({ onDeleteBatch }) => {
			const id = onDeleteBatch.id as UUID;
			this.removeItemOperationsByBatchId(id);
			this.removeBatch(id); // Remove from batches
		});
	}

	private addItemOperationsDynamically(groups: ItemOperationGroup[]): void {
		const nextGroups = [...this.groups];
		for (const incoming of groups) {
			const groupIndex = nextGroups.findIndex((g) => g.name.toUpperCase() === incoming.name.toUpperCase());
			if (groupIndex === -1) {
				nextGroups.push({ ...incoming, items: [...incoming.items] });
			} else {
				const mergedItems = [...nextGroups[groupIndex].items];
				incoming.items.forEach((op) => {
					const itemIndex = mergedItems.findIndex((x) => x.id === op.id);
					itemIndex === -1 ? mergedItems.push({ ...op }) : (mergedItems[itemIndex] = { ...op });
				});
				nextGroups[groupIndex] = { ...nextGroups[groupIndex], items: mergedItems };
			}
		}
		this.groups = nextGroups;
	}

	private removeItemOperationsByBatchId(batchId: UUID): void {
		const hasBatchId = this.groups.some((g) => g.items.some((i) => i.batchId === batchId));

		if (!hasBatchId) {
			return;
		}

		for (const group of this.groups) {
			group.items = group.items?.filter((op) => op.batchId !== batchId);
		}

		this.groups = this.groups.filter((g) => g.items.length > 0);

		// Reset current item if it no longer exists
		const stillHasCurrentItem = this.groups.some((g) => g.items.some((i) => i.id === this.currentItem.id));
		if (this.currentItem && !stillHasCurrentItem) this.currentItem = null;

		// Reset current group if it no longer exists
		const stillHasCurrentGroup = this.groups.some((g) => g.name === this.currentGroup.name && g.items.length > 0);
		if (this.currentGroup && !stillHasCurrentGroup) this.currentGroup = null;
	}

	private handleGraphQLEventItemOperationUpdated(op: IItemOperationResponse) {
		if (op.terminalId !== this.terminalId) {
			return;
		}

		if (this.completionGroupType === ICompletionGroupType.Locations) {
			this.handleLocationItemOperationUpdated(op);
			return;
		}

		const groupName = op.group || 'DEFAULT';
		const groupKey = groupName.toUpperCase();

		const group = this.groups.find((g) => (g.name || 'DEFAULT').toUpperCase() === groupKey);
		if (!group) {
			const parsedItem = this.parseItemOperation(op, op.itemData);
			const newGroup = {
				groupSortKey: op.groupSortKey || groupName,
				name: groupName,
				items: [parsedItem],
			};
			this.groups.push(newGroup);
			this._groupsUpdatedSubject.next(this.groups);
			return;
		}

		const item = group.items.find((i) => i.id === op.id);
		if (!item) {
			const parsedItem = this.parseItemOperation(op, op.itemData);
			group.items = [...group.items, parsedItem];
			this._someItemCreatedSubject.next(parsedItem);
			return;
		}

		item.items = op.items;

		if (!this._currentGroup) {
			this.currentGroup = group;
		}

		this._someItemUpdatedSubject.next(item);
	}

	private handleLocationItemOperationUpdated(itemOperation: IItemOperationResponse) {
		const parsedItem = this.parseItemOperation(itemOperation, itemOperation.itemData);
		const targetLocations = this.locationNamesByKey(parsedItem);
		const readyLocations = this.locationNamesByKey(parsedItem, true);
		const locationsWithItemOperation = new Set(this.groups.filter((g) => g.items.some((i) => i.id === itemOperation.id)).map((g) => g.name.toUpperCase()));

		const addsLocation = [...readyLocations.keys()].some((key) => !this.groups.some((g) => g.name.toUpperCase() === key));
		const emptiesLocation = this.groups.some((g) => locationsWithItemOperation.has(g.name.toUpperCase()) && !targetLocations.has(g.name.toUpperCase()) && g.items.length === 1);

		if (addsLocation || emptiesLocation) {
			this.groups = this.rebuildLocationGroups(parsedItem, targetLocations, readyLocations);
			this._someItemUpdatedSubject.next(parsedItem);
			return;
		}

		const updated: IItemOperation[] = [];
		const created: IItemOperation[] = [];

		for (const group of this.groups) {
			const key = group.name.toUpperCase();

			if (!targetLocations.has(key)) {
				if (locationsWithItemOperation.has(key)) group.items = group.items.filter((i) => i.id !== itemOperation.id);
				continue;
			}

			const items = parsedItem.items.filter((si) => this.locationKey(si).toUpperCase() === key);
			const existing = group.items.find((i) => i.id === itemOperation.id);

			if (existing) {
				existing.items = items;
				updated.push(existing);
			} else {
				const copy = { ...parsedItem, items };
				group.items = [...group.items, copy];
				created.push(copy);
			}

			if (!this._currentGroup) {
				this.currentGroup = group;
			}
		}

		this.whenItemHasBeenMovedToNewLocationSelectNewLocation(itemOperation.id);

		for (const item of updated) this._someItemUpdatedSubject.next(item);
		for (const item of created) this._someItemCreatedSubject.next(item);
	}

	private whenItemHasBeenMovedToNewLocationSelectNewLocation(itemOperationId: UUID) {
		if (!this.currentItem || this.currentItem.id !== itemOperationId) return;
		if (this.currentGroup?.items.some((i) => i.id === itemOperationId)) return;

		const group = this.groups.find((g) => g.items.some((i) => i.id === itemOperationId));

		if (!group) {
			this.currentItem = null;
			return;
		}

		this.currentGroup = group;
		this.currentItem = group.items.find((i) => i.id === itemOperationId);
	}

	private rebuildLocationGroups(parsedItem: IItemOperation, targetLocations: Map<string, string>, readyLocations: Map<string, string>): ItemOperationGroup[] {
		const next = this.groups.map((group) => ({
			...group,
			items: group.items.filter((i) => i.id !== parsedItem.id),
		}));

		for (const [key, name] of targetLocations) {
			let group = next.find((g) => g.name.toUpperCase() === key);
			if (!group) {
				if (!readyLocations.has(key)) continue;
				group = { groupSortKey: name, name, items: [] };
				next.push(group);
			}
			group.items.push({
				...parsedItem,
				items: parsedItem.items.filter((si) => this.locationKey(si).toUpperCase() === key),
			});
		}

		return next.filter((g) => g.items.length > 0);
	}

	private _terminalCancel$ = new Subject<void>();

	// Update existing item, fx. when status change
	private _someItemUpdatedSubject: Subject<IItemOperation> = new Subject();
	onSomeItemUpdated(): Observable<IItemOperation> {
		return this._someItemUpdatedSubject.asObservable();
	}

	// New item added to a group
	private _someItemCreatedSubject: Subject<IItemOperation> = new Subject();
	onSomeItemCreated(): Observable<IItemOperation> {
		return this._someItemCreatedSubject.asObservable();
	}

	getBatchesOfStatus(productionStatus: string | null): Observable<BatchResponse[]> {
		const url = `${environment.awsEnvUri}/mes/batches${productionStatus ? `?status=${productionStatus}` : ''}`;
		return this.http
			.get<{ status: number; result: BatchResponse[] }>(url, {
				headers: this.getHeaders(),
			})
			.pipe(map((response) => response.result));
	}

	getHeaders() {
		const userAuth = this.sessionStorageService.get('User.Authorization');
		const userAccount = JSON.parse(this.sessionStorageService.get('User.Account')) as IAccount;
		return {
			Authorization: `Bearer ${userAuth}`,
			'x-account-id': userAccount.id,
		};
	}

	// Uses REST API with Pagination.
	fetchItemOperationsForBatch(terminalId: UUID, batchId: UUID, status: string): Observable<ItemOperationGroup[]> {
		if (!terminalId || !batchId) return of([]);

		const url = `${environment.awsEnvUri}/mes/terminals/${terminalId}/batches/${batchId}/itemoperations?status=${status}&limit=1000`;
		return this.http
			.get<{ result: IRESTPaginatedItemOperationResponse }>(url, {
				headers: this.getHeaders(),
			})
			.pipe(
				expand(({ result }) => (result.nextToken ? this.http.get<{ result: IRESTPaginatedItemOperationResponse }>(`${url}&nextToken=${result.nextToken}`, { headers: this.getHeaders() }) : EMPTY)),
				map(({ result }) => result.items),
				mergeMap((items) => items),
				toArray(),
				map((items) => {
					return this.groupItems(items);
				}),
			);
	}

	private getDetail(batchId: UUID, alias: string): Observable<IDetailData> {
		const url = `${environment.awsEnvUri}/mes/batches/${batchId}/detaildata/${alias}`;
		return this.http.get<{ result: IDetailData }>(url, { headers: this.getHeaders() }).pipe(map((c) => c.result));
	}

	private getPredecessorItemData(nextId: UUID): Observable<IItemOperationByNextIdResponse> {
		const url = `${environment.awsEnvUri}/mes/itemoperations/${nextId}`;
		return this.http.get<{ result: IItemOperationByNextIdResponse }>(url, { headers: this.getHeaders() }).pipe(map((c) => c.result));
	}

	// Uses GraphQL Merged API with Pagination.
	public fetchItemOperationsGraphQL(terminalId: string, batchId: string) {
		const fetchPage = (nextToken?: string) => this.getItemOperationsQuery.input({ terminalId, batchId, limit: 1000, nextToken }).query<IPaginatedItemOperationResponse>();
		return fetchPage().pipe(
			expand((response) => (response.itemOperations.nextToken ? fetchPage(response.itemOperations.nextToken) : EMPTY)),
			mergeMap((response) => response.itemOperations.items),
			toArray(),
			map((allItems) => this.groupItems(allItems)),
		);
	}

	private groupItems(items: IItemOperationResponse[]): ItemOperationGroup[] {
		if (this.completionGroupType === ICompletionGroupType.Locations) {
			return this.groupItemsByLocation(items);
		}

		return items.reduce((groups, item) => {
			const parsedItem = this.parseItemOperation(item, item.itemData);
			const groupName = item.group || 'DEFAULT';
			const groupSortKey = item.groupSortKey || item.group || '';
			let group = groups.find((g) => g.name === groupName);
			if (!group) {
				group = { groupSortKey, name: groupName, items: [] };
				groups.push(group);
			}
			group.items.push(parsedItem);
			return groups;
		}, [] as ItemOperationGroup[]);
	}

	private groupItemsByLocation(items: IItemOperationResponse[]): ItemOperationGroup[] {
		return items.reduce((groups, item) => {
			const parsedItem = this.parseItemOperation(item, item.itemData);

			for (const [key, name] of this.locationNamesByKey(parsedItem, true)) {
				let group = groups.find((g) => g.name.toUpperCase() === key);
				if (!group) {
					group = { groupSortKey: name, name, items: [] };
					groups.push(group);
				}
				group.items.push({
					...parsedItem,
					items: parsedItem.items.filter((si) => this.locationKey(si).toUpperCase() === key),
				});
			}

			return groups;
		}, [] as ItemOperationGroup[]);
	}

	private locationNamesByKey(itemOperation: IItemOperation, readyOnly = false): Map<string, string> {
		const names = new Map<string, string>();
		for (const item of itemOperation.items) {
			if (readyOnly && !this.showNonReady && !item.ready) continue;
			const name = this.locationKey(item);
			if (!names.has(name.toUpperCase())) names.set(name.toUpperCase(), name);
		}
		return names;
	}

	private locationKey(item: IItemOperationItem): string {
		const name = item.location?.name;
		return name?.trim() ? name : 'DEFAULT';
	}

	parseItemOperation(item: IBaseItemOperation, data?: string): IItemOperation {
		return { ...item, itemData: data ? JSON.parse(data) : {} };
	}

	//
	// Getters and setters and subjects
	//

	// Terminals

	private _stationNumber: number;
	public get stationNumber(): number {
		return this._stationNumber;
	}

	// IMPORTANT: Must ONLY be set from data view component!
	public set stationNumber(value: number) {
		this._stationNumber = value;
		this._stationNumberChangedSubject.next(value);
	}

	private _stationNumberChangedSubject: Subject<number> = new Subject();
	onStationNumberChanged(): Observable<number> {
		return this._stationNumberChangedSubject;
	}

	private _terminalId: UUID;
	public get terminalId(): UUID {
		return this._terminalId;
	}

	// IMPORTANT: Must ONLY be set from data view component!
	public set terminalId(value: UUID | undefined) {
		const reset = (loading: boolean) => {
			this.loadingData = loading;
			this._detailDataCache.clear();
			this.groups = [];
			this.batches = [];
			this._currentGroup = null;
			this._currentItem = null;
		};

		// Cancel current data fetch when new terminal chosen
		this._terminalCancel$.next();

		if (!value) {
			this._terminalId = null;
			this._stationNumber = 1;
			reset(false);
			return;
		}

		this._terminalId = value;
		reset(true);

		this.stationNumber = Number.parseInt(this.localStorageService.get(`station:${value}`)) ?? 1;

		const key = `${this.terminalId}#show-non-ready`;
		this.showNonReady = this.localStorageService.get(key) === 'true';
		const status = this.showNonReady ? 'all' : 'someready';
		this.getBatchesOfStatus('production')
			.pipe(
				tap((batches) => (this.batches = batches)),
				switchMap((batches) => (batches.length ? forkJoin(batches.map((b) => this.fetchItemOperationsForBatch(this.terminalId, b.id, status))) : of([] as ItemOperationGroup[]))),
				map((results) => results.flat()),
				takeUntil(this._terminalCancel$),
				finalize(() => {
					this.loadingData = false;
				}),
			)
			.subscribe({
				next: (groups) => this.addItemOperationsDynamically(groups),
				error: (err) => console.log(JSON.stringify(err)),
			});
	}

	private _batches: BatchResponse[] = [];
	public get batches(): BatchResponse[] {
		return this._batches;
	}
	public set batches(value: BatchResponse[]) {
		this._batches = value;
		this._batchesSubject.next(value);
	}

	private _batchesSubject = new Subject<BatchResponse[]>();
	onBatchesChanged(): Observable<BatchResponse[]> {
		return this._batchesSubject.asObservable();
	}

	private upsertBatch(b: BatchResponse): void {
		const i = this._batches.findIndex((x) => x.id === b.id);
		this.batches = i === -1 ? [...this._batches, b] : this._batches.map((x) => (x.id === b.id ? b : x));
	}

	private removeBatch(id: UUID): void {
		if (!this._batches.length) return;
		this.batches = this._batches.filter((x) => x.id !== id);
	}

	public getBatches(status: string | null = 'production'): void {
		this.loadingData = true;
		this.getBatchesOfStatus(status)
			.pipe(
				finalize(() => {
					this.loadingData = false;
				}),
			)
			.subscribe({
				next: (batches) => (this.batches = batches),
				error: (err) => console.log(JSON.stringify(err)),
			});
	}

	private _groups: ItemOperationGroup[] = [];
	public get groups(): ItemOperationGroup[] {
		return this._groups;
	}

	public set groups(value: ItemOperationGroup[]) {
		this._groups = value;
		this._groupsUpdatedSubject.next(value);
	}

	private _groupsUpdatedSubject: Subject<ItemOperationGroup[]> = new Subject();
	onGroupsUpdated(): Observable<ItemOperationGroup[]> {
		return this._groupsUpdatedSubject;
	}

	// Current group
	private _currentGroup: ItemOperationGroup;
	public get currentGroup(): ItemOperationGroup {
		return this._currentGroup;
	}

	public set currentGroup(value: ItemOperationGroup) {
		if (!value) {
			this._currentGroup = null;
			this._currentGroupSubject.next(value);
			this.currentItem = null;
			return;
		}

		if (this._currentGroup === value) {
			return;
		}

		this._currentGroup = value;
		this._currentGroupSubject.next(value);
	}

	private _currentGroupSubject: Subject<ItemOperationGroup> = new Subject();
	onCurrentGroupChanged(): Observable<ItemOperationGroup> {
		return this._currentGroupSubject;
	}

	// Current item
	private _currentItem: IItemOperation;
	public get currentItem(): IItemOperation {
		return this._currentItem;
	}

	public set currentItem(value: IItemOperation) {
		if (!value) {
			this._currentItem = null;
			this._currentItemSubject.next(value);
			this.currentDetailData = null;
			return;
		}

		const wantsDetails = !!this.settings.getDetails;
		const detailAlias = value?.detailAlias;

		if (!detailAlias || !wantsDetails) {
			this._currentItem = value;
			this._currentItemSubject.next(value);
			this.currentDetailData = null;
			return;
		}

		const detailData$ = this._detailDataCache.has(detailAlias)
			? of(this._detailDataCache.get(detailAlias))
			: this.getDetail(value.batchId, detailAlias).pipe(
					tap((c) => {
						const data = JSON.parse(c.data);
						if (data) {
							this._detailDataCache.set(detailAlias, data);
						}
					}),
					map((c) => JSON.parse(c.data)),
				);

		detailData$
			.pipe(
				switchMap((detailData) =>
					this.getPredecessorItemData(value.id).pipe(
						map((predecessorData) => {
							const parsedPredecessors = Object.values(predecessorData?.itemDataFromPredecessors || {}).map((val) => JSON.parse(val));
							return { ...detailData, itemDataFromPredecessors: parsedPredecessors };
						}),
					),
				),
			)
			.subscribe({
				next: (mergedData) => {
					this._currentItem = value;
					this._currentItemSubject.next(value);
					this.currentDetailData = mergedData;
				},
				error: (err) => console.log(JSON.stringify(err)),
			});
	}

	private _currentItemSubject: Subject<IItemOperation> = new Subject();
	onCurrentItemChanged(): Observable<IItemOperation> {
		return this._currentItemSubject;
	}

	// Current detail data
	private _currentDetailData: any;
	public get currentDetailData(): any {
		return this._currentDetailData;
	}

	public set currentDetailData(value: any) {
		this._currentDetailData = value;
		this._currentDetailDataSubject.next(value);
	}

	private _currentDetailDataSubject: Subject<any> = new Subject();
	onCurrentDetailDataUpdated(): Observable<any> {
		return this._currentDetailDataSubject.asObservable();
	}

	private _detailDataCache = new Map<string, any>();

	//
	// End of getters and setters and subjects
	//

	reorderItemsOnItemOperation(itemOperation: IItemOperation, item: IItemOperationItem, note: IProductionNotePostRequest, incidentCodeId: UUID): Observable<IReorderedItemOperationResponse | null> {
		if (!item.ready) {
			return of(null);
		}
		const url = `${environment.awsEnvUri}/mes/itemoperations/${itemOperation.id}/reorders`;
		const body = {
			reorderItemOperation: {
				sequenceNumber: item.sequenceNumber,
				reorderItems: [{ item: itemOperation.alias }],
				incidentCodeId,
			},
			productionNote: note,
		};
		// OBS: The result of this observable should not be used to update anything!
		//      We instead use GraphQL subscriptions for updating entities based on backend events.
		return this.http.post<IReorderedItemOperationResponse>(url, body, {
			headers: this.getHeaders(),
		});
	}

	completeItemsOnItemOperation(itemOperation: IItemOperation, items: IItemOperationItem[], complete: boolean = true): Observable<ICompletedItemOperationResponse | null> {
		const elements = items.filter((e) => (complete ? e.ready : e.completed));
		if (!elements.length) {
			return of(null);
		}
		const url = `${environment.awsEnvUri}/mes/itemoperations/${itemOperation.id}/${complete ? 'completes' : 'uncompletes'}`;
		const data = {
			sequenceNumbers: elements.map((e) => e.sequenceNumber),
		};
		// OBS: The result of this observable should not be used to update anything!
		//      We instead use GraphQL subscriptions for updating entities based on backend events.
		return this.http.post<ICompletedItemOperationResponse>(url, data, {
			headers: this.getHeaders(),
		});
	}

	getItemOperation(id: UUID): Observable<IItemOperationByIdResponse> {
		const url = `${environment.awsEnvUri}/mes/itemoperations/${id}`;
		// OBS: The result of this observable should not be used to update anything!
		//      We instead use GraphQL subscriptions for updating entities based on backend events.
		return this.http.get<IItemOperationByIdResponse>(url, { headers: this.getHeaders() });
	}
}
